import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { CASES } from "@/data/cases";
import { caseTexts } from "@/data/caseTexts";
import { useLanguage } from "@/i18n/LanguageContext";

const CardInner = ({ c }) => {
  const { t } = useLanguage();

  return (
  <>
    <div className="relative overflow-hidden rounded-2xl border border-white/10">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={c.image}
          alt={`${c.title} — ${c.category}`}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <span className="absolute left-6 top-6 font-syne text-sm uppercase tracking-[0.3em] text-white/70">
        {c.no}
      </span>
      {!c.demo && (
        <span className="absolute right-6 top-6 rounded-full border border-white/30 bg-black/40 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/80 backdrop-blur-md">
          {t("gallery.badge")}
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF3333]">{c.category}</p>
          <h3 className="mt-1 font-syne text-3xl font-bold tracking-tight text-white">{c.title}</h3>
        </div>
        {c.demo && (
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-white transition-all duration-300 group-hover:border-[#FF3333] group-hover:bg-[#FF3333]">
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        )}
      </div>
    </div>
    <p className="mt-5 max-w-md text-sm leading-relaxed text-[#A3A3A3]">{c.description}</p>
  </>
  );
};

const CaseCard = ({ c }) =>
  c.demo ? (
    <Link to={`/demo/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group block">
      <CardInner c={c} />
    </Link>
  ) : (
    <div data-testid={`case-card-${c.slug}`} className="group block">
      <CardInner c={c} />
    </div>
  );

export const Gallery = () => {
  const { t, lang } = useLanguage();
  const localized = CASES.map((c) => ({
    ...c,
    category: caseTexts[lang]?.[c.slug]?.category ?? c.category,
    description: caseTexts[lang]?.[c.slug]?.description ?? c.description,
  }));

  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    let last = 0;
    const step = (time) => {
      if (!pausedRef.current && time - last > 30) {
        last = time;
        el.scrollLeft += 0.5;
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resumeTimer.current);
    };
  }, []);

  const pause = () => {
    pausedRef.current = true;
    clearTimeout(resumeTimer.current);
  };
  const resume = () => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      pausedRef.current = false;
    }, 1200);
  };

  const scrollTrack = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(":scope > div");
    const w = card ? card.getBoundingClientRect().width + 32 : 520;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
  <section id="cases" data-testid="gallery-section" className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
    <span className="text-sm uppercase tracking-[0.3em] text-[#FF3333]">{t("gallery.label")}</span>
    <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tighter text-[#F2F2F2]">
      <MaskedLine innerClassName="text-4xl sm:text-5xl lg:text-6xl">{t("gallery.h1")}</MaskedLine>
      <MaskedLine delay={0.12} innerClassName="text-4xl sm:text-5xl lg:text-6xl text-stroke">
        {t("gallery.h2")}
      </MaskedLine>
    </h2>
    <FadeUp delay={0.2} className="mt-6 max-w-lg">
      <p className="text-base text-[#A3A3A3] md:text-lg">
        {t("gallery.intro")}
      </p>
    </FadeUp>

    <FadeUp delay={0.3}>
      <div className="mt-16">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
            {localized.length} / {localized.length}
          </p>
          <div className="flex gap-3">
            <button
              data-testid="carousel-prev"
              onClick={() => scrollTrack(-1)}
              aria-label="Previous"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-[#F2F2F2] transition-colors duration-300 hover:border-[#FF3333] hover:text-[#FF3333]"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="carousel-next"
              onClick={() => scrollTrack(1)}
              aria-label="Next"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-[#F2F2F2] transition-colors duration-300 hover:border-[#FF3333] hover:text-[#FF3333]"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div
          ref={trackRef}
          data-testid="gallery-carousel"
          data-lenis-prevent
          onPointerEnter={pause}
          onPointerLeave={resume}
          onTouchStart={pause}
          onTouchEnd={resume}
          className="scrollbar-hide -mx-6 flex gap-8 overflow-x-auto px-6 pb-4 md:-mx-12 md:px-12"
        >
          {localized.map((c) => (
            <div key={c.slug} className="w-[85vw] max-w-[520px] shrink-0">
              <CaseCard c={c} />
            </div>
          ))}
        </div>
      </div>
    </FadeUp>
  </section>
  );
};
